import { useState, useEffect } from "react";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TipTapLink from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import {
  StickyNote, Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, X, Check, Loader2,
} from "lucide-react";
import { toast } from "./ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadNoteEditor({ open, onClose, leadId, tc500, onSaved, editNote }) {
  const [saving, setSaving] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TipTapLink.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: "Escreva sua anotação..." }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "focus:outline-none min-h-[160px] max-h-[300px] overflow-y-auto px-4 py-3 text-sm text-slate-800",
      },
    },
  });

  useEffect(() => {
    if (open && editor) {
      editor.commands.setContent(editNote?.corpo_html || "");
    }
  }, [open, editor, editNote]);

  const handleClose = () => {
    if (editor) editor.commands.setContent("");
    onClose(false);
  };

  const handleSave = async () => {
    const html = editor?.getHTML() || "";
    if (!html || html === "<p></p>") return;
    setSaving(true);
    try {
      if (editNote) {
        await axios.put(`${API}/leads/${leadId}/notes/${editNote.id}`, { corpo_html: html });
        toast.success("Anotação atualizada!");
      } else {
        await axios.post(`${API}/leads/${leadId}/notes`, { corpo_html: html });
        toast.success("Anotação salva!");
      }
      if (editor) editor.commands.setContent("");
      onSaved();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  if (!editor) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-lg !p-0" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle className="font-heading flex items-center gap-2">
            <StickyNote className="w-5 h-5" style={{ color: tc500 }} />
            {editNote ? "Editar anotação" : "Nova anotação"}
          </DialogTitle>
        </DialogHeader>
        <div className="px-5 pb-5 space-y-3">
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
              <button type="button" onClick={() => editor.chain().focus().toggleBold().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${editor.isActive("bold") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <Bold className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${editor.isActive("italic") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <Italic className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${editor.isActive("underline") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <UnderlineIcon className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${editor.isActive("strike") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <Strikethrough className="w-3.5 h-3.5" />
              </button>
              <div className="w-px h-5 bg-slate-300/50 mx-0.5" />
              <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${editor.isActive("bulletList") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <List className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${editor.isActive("orderedList") ? "bg-slate-300/60 text-slate-900" : "text-slate-500 hover:bg-slate-200/60"}`}>
                <ListOrdered className="w-3.5 h-3.5" />
              </button>
            </div>
            <EditorContent editor={editor} />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" className="flex-1" onClick={handleClose}>
              <X className="w-4 h-4 mr-1" />Cancelar
            </Button>
            <Button type="button" className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }}
              disabled={saving} onClick={handleSave}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
